/**
 * useEntitlement - Course access and download entitlements
 *
 * Two concerns handled here:
 * 1. **Course Access**: Can user access course content (and how much)?
 *    - Community/free courses: Full access
 *    - Premium courses: Free through Yellow Belt, then requires subscription
 *
 * 2. **Download Entitlement**: Can user download for offline use?
 *    - Free users: Auto-prefetch ~30 minutes ahead
 *    - Paid users: Full course download
 *
 * Access is checked at playback; entitlement is checked before caching.
 */

import { ref, computed, inject, type Ref, type ComputedRef } from 'vue'
import { useSharedSubscription } from './useSubscription'
import { useSharedUserEntitlements } from './useUserEntitlements'
import { useUserRole } from './useUserRole'
import { pupilClassIdState, pupilCourseCodesState } from './useVoiceEntitlement'
import { legacyAnswerPending } from './legacyAccessNote'
import type { CoursePricingTier } from '@ssi/core'
import {
  checkCourseAccess,
  holdsOfflineCapability,
  inferPricingTier,
  isFreeTierCourse,
  isOfflineTeacherRole,
  PREMIUM_PREVIEW_MAX_SEED,
  SCHOOL_PUPIL_FREE_MAX_SEED,
  type CourseAccessResult,
} from '@ssi/core'

// ============================================================================
// CLIENT-SIDE ENTITLEMENT FLAG (UI gating only — server is the authority)
// ============================================================================

/**
 * Does this browser hold a grant that should unlock premium UI?
 *
 * PROD: the preferred grant is a server-minted, time-boxed try-link token
 * (`ssi-try-token`, stored by TryLinkGateway). This drives UI only; the audio
 * proxy re-verifies the token server-side before serving premium-past-preview
 * content, so the raw flag below can never actually unlock premium AUDIO once
 * `ENTITLEMENT_ENFORCE=strict` is armed — it only affects UI affordances.
 *
 * TRANSITION ALLOWANCE: we still honour the legacy raw `ssi-demo-tier=paid`
 * flag in prod. The live `/demo` sales flow (DemoLauncher, out of this lane)
 * and any in-flight try-link sessions that validated before this deploy set
 * only that flag; dropping it would strand the demo's premium UI unlock and
 * any visitor mid-session. This is safe because the SERVER (audio proxy) is the
 * real paywall — a raw flag yields no premium audio under strict mode. Remove
 * this allowance once DemoLauncher mints a real `ssi-try-token`.
 *
 * DEV: keep the additional convenience flags so local testing is frictionless.
 */
export function hasTryEntitlement(): boolean {
  try {
    const token = sessionStorage.getItem('ssi-try-token')
    const exp = Number(sessionStorage.getItem('ssi-try-exp') || '0')
    if (token && exp > Date.now()) return true
    // Honoured in prod AND dev (transition: /demo + in-flight try-link sessions).
    if (sessionStorage.getItem('ssi-demo-tier') === 'paid') return true
    if (import.meta.env.PROD) return false
    // DEV-only conveniences below.
    if (localStorage.getItem('ssi-dev-tier') === 'paid') return true
    return localStorage.getItem('ssi-dev-paid-user') === 'true'
  } catch {
    return false
  }
}

// ============================================================================
// TYPES
// ============================================================================

export interface EntitlementStatus {
  /** Can user download for offline use? */
  canDownload: boolean
  /** Is user authenticated? */
  isAuthenticated: boolean
  /** Does user have paid subscription? */
  isPaidUser: boolean
  /** Course-specific entitlement */
  courseEntitlement: 'free' | 'paid' | 'community'
  /** Maximum hours user can download (paid users get more) */
  maxDownloadHours: number
}

export interface CourseInfo {
  course_code: string
  target_lang?: string
  known_lang?: string
  pricing_tier?: CoursePricingTier
  is_community?: boolean
}

export interface UseEntitlementReturn {
  /** Current entitlement status (for downloads) */
  entitlement: Ref<EntitlementStatus>
  /** Whether user can download this course */
  canDownload: ComputedRef<boolean>
  /** Maximum download hours allowed */
  maxDownloadHours: ComputedRef<number>
  /** Check entitlement for a course (legacy - for downloads) */
  checkEntitlement: (courseId: string, courseInfo?: CourseInfo) => Promise<EntitlementStatus>
  /** Refresh entitlement (e.g., after purchase) */
  refreshEntitlement: () => Promise<void>

  // New access control methods
  /** Check course access (for content gating) */
  checkCourseAccess: (course: CourseInfo) => CourseAccessResult
  /** Check if user can access a specific seed */
  canAccessSeed: (course: CourseInfo, seedNumber: number) => boolean
  /** Signed in and not a known payer, with the subscription or the entitlement
   *  answer not yet in for this identity: access checks answer 'pending' —
   *  the preview, no wall. Callers hold at the preview edge (job #895). */
  accessPending: () => boolean
  /** Signed in and the old SSi app has never answered for this learner (ask
   *  681): the wall is not raised on that, the player holds and asks there. */
  oldAppPending: () => boolean
  /** Signed in and the server's subscription answer has not landed this
   *  session — whatever the localStorage mirror says. The predicate every
   *  cursor writer and the resume gate wait on (job #778). */
  verdictPending: () => boolean
  /** The negation of verdictPending, as a ref to watch: flips true when both
   *  answers are in for this identity. */
  verdictKnown: ComputedRef<boolean>
  subscriptionHydrated: Ref<boolean>
  /** Get preview limit for premium courses */
  getPreviewLimit: () => number
  /** Can the user START an offline download? A learner holding offline on this
   *  course, or anyone on a FREE course (the one 30-day window; LearningPlayer
   *  raises the paywall once the lease store says it is spent). */
  canDownloadOffline: (course: CourseInfo) => boolean
  /** Does this learner's offline RENEW on this course (payer; teacher on a free
   *  or covered course)? False = the free window on a free course, else none. */
  offlineRenews: (course: CourseInfo) => boolean
}

// ============================================================================
// CONSTANTS
// ============================================================================

/** Free users can auto-prefetch this many minutes ahead */
export const FREE_PREFETCH_MINUTES = 30

/** Paid users can download this many hours */
export const PAID_DOWNLOAD_HOURS = 10

/** Community courses are always free to download */
export const COMMUNITY_DOWNLOAD_HOURS = 10

// ============================================================================
// COMPOSABLE
// ============================================================================

export function useEntitlement(): UseEntitlementReturn {
  // Get auth from app context
  const auth = inject<{ isAuthenticated: Ref<boolean>; learnerId: Ref<string | null> } | null>('auth', null)

  // Get subscription status
  const { isSubscribed: hasActiveSubscription, hasHydrated: subscriptionHydrated } = useSharedSubscription()

  // Get user entitlements from entitlement codes
  const { entitlements: userEntitlements, isKnown: entitlementsKnown } = useSharedUserEntitlements()

  // Get user role (ssi_admin/god bypasses all access checks)
  const { platformRole, educationalRole } = useUserRole()

  // Both answers in for this identity (or a guest, who has nothing to wait for).
  const verdictKnown = computed(
    () => !auth?.isAuthenticated.value || (subscriptionHydrated.value && entitlementsKnown.value),
  )

  // Entitlement state
  const entitlement = ref<EntitlementStatus>({
    canDownload: false,
    isAuthenticated: false,
    isPaidUser: false,
    courseEntitlement: 'free',
    maxDownloadHours: 0,
  })

  // Current course being checked
  const currentCourseId = ref<string | null>(null)
  const currentCourseInfo = ref<CourseInfo | null>(null)

  // ============================================================================
  // COMPUTED
  // ============================================================================

  const canDownload = computed(() => entitlement.value.canDownload)
  const maxDownloadHours = computed(() => entitlement.value.maxDownloadHours)

  // ============================================================================
  // SUBSCRIPTION HELPERS
  // ============================================================================

  /**
   * Get subscription status for access checks
   */
  function getSubscriptionStatus() {
    const isPaid = hasActiveSubscription.value || checkDevPaidStatus()
    return {
      isActive: isPaid,
      tier: isPaid ? 'paid' as const : 'free' as const,
      // Authenticated but an answer not yet in for this identity (fresh load,
      // ?reset=1, a sign-in, a class join). UNKNOWN IS WAITING (job #895):
      // checkCourseAccess answers 'pending' — the preview and no upgrade page,
      // never paid content on a guess and never a padlock that flashes open.
      isPending: !isPaid && !!auth?.isAuthenticated.value && (!subscriptionHydrated.value || !entitlementsKnown.value),
    }
  }

  /**
   * Check localStorage dev flags for testing.
   * Checks both legacy ssi-dev-paid-user and new ssi-dev-tier.
   */
  function checkDevPaidStatus(): boolean {
    return hasTryEntitlement()
  }

  // ============================================================================
  // ACCESS CONTROL (new - database-driven)
  // ============================================================================

  /**
   * Check course access using database pricing_tier or inferred tier
   *
   * @param course - Course with pricing info (from database or inferred)
   * @returns CourseAccessResult with access level and reason
   */
  function checkAccess(course: CourseInfo): CourseAccessResult {
    // Use database pricing_tier if available, otherwise infer from course code
    const pricingTier = course.pricing_tier
      ?? inferPricingTier(course.target_lang ?? '', course.course_code)

    const isCommunity = course.is_community ?? course.course_code.startsWith('community_')

    const courseWithPricing = {
      course_code: course.course_code,
      pricing_tier: pricingTier,
      is_community: isCommunity,
    }

    const subscription = getSubscriptionStatus()
    return checkCourseAccess(courseWithPricing, subscription, userEntitlements.value, platformRole.value)
  }

  /**
   * Check if user can access a specific seed
   *
   * @param course - Course info
   * @param seedNumber - Seed number (1-based)
   * @returns true if user can access this seed
   */
  function canAccessSeedCheck(course: CourseInfo, seedNumber: number): boolean {
    const access = checkAccess(course)

    // Full access
    if (access.canAccess) {
      return true
    }

    // Preview mode - check limit. Seed ID, not running-order position (job
    // #949): kept in step with the server ceiling in api/courses/[code]/
    // cycles.ts + bundle.ts, which say why the id is safe for today's weave.
    if (access.canPreview && access.previewMaxSeed) {
      // A class-joined pupil plays the CLASS'S OWN course through the end of
      // Green (job #97); every other premium course keeps the end-of-Yellow
      // preview so the green-belt stop can pitch it (Tom, 2026-10-01).
      const limit = pupilClassIdState.value && pupilCourseCodesState.value.includes(course.course_code)
        ? Math.max(access.previewMaxSeed, SCHOOL_PUPIL_FREE_MAX_SEED)
        : access.previewMaxSeed
      return seedNumber <= limit
    }

    return false
  }

  /**
   * Get the preview limit (max seed for free preview)
   */
  function getPreviewLimit(): number {
    return PREMIUM_PREVIEW_MAX_SEED
  }

  /** Free course: the DB tier if we have it, else the same inference checkAccess uses. */
  function isFreeCourse(course: CourseInfo): boolean {
    return isFreeTierCourse({
      pricing_tier: course.pricing_tier ?? inferPricingTier(course.target_lang ?? '', course.course_code),
      is_community: course.is_community ?? course.course_code.startsWith('community_'),
    })
  }

  /**
   * Can the user START an offline download on this course right now?
   *
   * Tom's rulings, 2026-09-28 and the correction of 2026-09-29 (the full rule
   * is in @ssi/core offlineCapability.ts): a learner who holds offline on the
   * course always may; anyone else may on a FREE course, inside their one
   * 30-day window from first offline use, which is the upgrade moment. Whether
   * that window is already spent is per-learner lease state this stateless
   * check does not hold, so LearningPlayer's download trigger asks the lease
   * store and raises the paywall once it is; the server lease route is the
   * authority either way.
   */
  function canDownloadOffline(course: CourseInfo): boolean {
    return offlineRenews(course) || isFreeCourse(course)
  }

  /**
   * Does this learner's offline RENEW on this course, rather than riding the
   * free account's one 30-day window? True for a payer in every course (either
   * plan, OFFLINE_UNLOCKED_BY), and for a teacher on every free course and on
   * a premium course their school's or class's cover grants. A course grant
   * alone, the way a school LEARNER's access arrives, renews nothing (Tom,
   * 2026-09-29: "learners, no, unless they have got the premium account that
   * the school enabled"); that premium account is a 'full' grant and counts as
   * the premium plan.
   */
  function offlineRenews(course: CourseInfo): boolean {
    const role = platformRole.value
    const now = new Date()
    const live = (userEntitlements.value || []).filter((e) => !(e.expiresAt && new Date(e.expiresAt) <= now))
    const sub = getSubscriptionStatus()
    const capable = holdsOfflineCapability({
      privileged: role === 'ssi_admin' || role === 'tester',
      premiumPlan: (sub.isActive && sub.tier === 'paid') || live.some((e) => e.accessType === 'full'),
      voicePlan: live.some((e) => e.accessType === 'voice'),
    })
    if (capable) return true
    if (!isOfflineTeacherRole(educationalRole.value)) return false
    if (isFreeCourse(course)) return true
    return live.some((e) =>
      e.accessType === 'courses' && !!e.grantedCourses && !!course.course_code && e.grantedCourses.includes(course.course_code),
    )
  }

  // ============================================================================
  // DOWNLOAD ENTITLEMENT (legacy interface - still needed for offline)
  // ============================================================================

  /**
   * Check download entitlement for a specific course.
   *
   * Entitlement logic:
   * 1. Community courses: Always free to download
   * 2. Paid user: Full download access
   * 3. Free user: Limited prefetch only (no explicit download)
   */
  async function checkEntitlement(
    courseId: string,
    courseInfo?: CourseInfo
  ): Promise<EntitlementStatus> {
    currentCourseId.value = courseId
    currentCourseInfo.value = courseInfo ?? { course_code: courseId }
    const isAuthenticated = auth?.isAuthenticated.value ?? false

    // Use new access check to determine entitlement
    const access = checkAccess(currentCourseInfo.value)

    // Map access result to entitlement
    if (access.reason === 'community') {
      entitlement.value = {
        canDownload: true,
        isAuthenticated,
        isPaidUser: false,
        courseEntitlement: 'community',
        maxDownloadHours: COMMUNITY_DOWNLOAD_HOURS,
      }
    } else if (access.reason === 'free') {
      // Free tier courses - full download access
      entitlement.value = {
        canDownload: true,
        isAuthenticated,
        isPaidUser: false,
        courseEntitlement: 'free',
        maxDownloadHours: COMMUNITY_DOWNLOAD_HOURS,
      }
    } else if (access.reason === 'subscribed' || access.reason === 'entitled') {
      // Paid user or entitled (admin/entitlement code) - full access
      entitlement.value = {
        canDownload: true,
        isAuthenticated,
        isPaidUser: true,
        courseEntitlement: 'paid',
        maxDownloadHours: PAID_DOWNLOAD_HOURS,
      }
    } else {
      // Preview only - no explicit download, just prefetch
      entitlement.value = {
        canDownload: false,
        isAuthenticated,
        isPaidUser: false,
        courseEntitlement: 'free',
        maxDownloadHours: 0,
      }
    }

    return entitlement.value
  }

  /**
   * Refresh entitlement (e.g., after user upgrades)
   */
  async function refreshEntitlement(): Promise<void> {
    if (currentCourseId.value) {
      await checkEntitlement(currentCourseId.value, currentCourseInfo.value ?? undefined)
    }
  }

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Download entitlement (legacy)
    entitlement,
    canDownload,
    maxDownloadHours,
    checkEntitlement,
    refreshEntitlement,

    // Access control (new)
    checkCourseAccess: checkAccess,
    canAccessSeed: canAccessSeedCheck,
    /** True while an answer is still out (signed in, not a known payer):
     *  every access check above answers 'pending' in that window — the
     *  preview, no wall (job #895). A caller that meets the preview edge in
     *  it HOLDS and waits on `verdictKnown`, rather than raising the wall.
     *  Bounded by both stores' 8s timeouts, so it always flips. */
    accessPending: () => getSubscriptionStatus().isPending,
    /** Trust until the old app says no (ask 681, Tom 2026-10-21): a learner
     *  the old app has never answered for is not told to upgrade yet. The
     *  wall waits on the answer, asked for at the wall (settleOldApp). */
    oldAppPending: () => !!auth?.isAuthenticated.value && legacyAnswerPending.value,
    /** The verdict is PENDING for every signed-in learner until `/api/subscription`
     *  has answered this session (or useSubscription's 8s bound fails closed).
     *  NOT `accessPending`: that one is false the moment the localStorage
     *  mirror says "active", because `isPending` is derived from `!isPaid` —
     *  so a lapsed subscriber whose device still holds last month's "active"
     *  mirror read as "verdict in" before any answer arrived, the resume gate
     *  ran on the stale mirror with no hold, and the cursor writers wrote
     *  behind it (Astra's cold verify of #768, confirmed job #778). Writers
     *  and the gate wait on THIS; access checks keep the mirror's optimism.
     *  Since job #895 the entitlement list's answer counts too: a class join
     *  or sign-in re-asks both, and the gate waits for both. */
    verdictPending: () => !verdictKnown.value,
    verdictKnown,
    subscriptionHydrated,
    getPreviewLimit,
    canDownloadOffline,
    offlineRenews,
  }
}

export type EntitlementComposable = ReturnType<typeof useEntitlement>
